// src/pages/TrocarSenhaObrigatoria.js

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/useToast';
import { completeCompulsoryPasswordChangeRemote } from '../services/cloudFunctions';
import { FaEye, FaEyeSlash, FaLock, FaCheckCircle, FaSpinner } from 'react-icons/fa';
import './TrocarSenhaObrigatoria.css';

const validatePasswordPolicy = (val) => {
    if (!val) return 'A senha é obrigatória.';
    if (val.length < 12) return 'A senha deve ter no mínimo 12 caracteres.';
    if (!/[A-Z]/.test(val)) return 'A senha deve conter pelo menos uma letra maiúscula.';
    if (!/[a-z]/.test(val)) return 'A senha deve conter pelo menos uma letra minúscula.';
    if (!/[0-9]/.test(val)) return 'A senha deve conter pelo menos um número.';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?]/.test(val)) return 'A senha deve conter pelo menos um caractere especial.';
    return true;
};

const TrocarSenhaObrigatoria = () => {
    const { logout } = useAuth();
    const { addToast } = useToast();

    const [novaSenha, setNovaSenha] = useState('');
    const [confirmarSenha, setConfirmarSenha] = useState('');
    const [showNova, setShowNova] = useState(false);
    const [showConfirmar, setShowConfirmar] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (!novaSenha || !confirmarSenha) {
            setErrorMsg('Preencha todos os campos.');
            return;
        }

        if (novaSenha !== confirmarSenha) {
            setErrorMsg('A nova senha e a confirmação não conferem.');
            return;
        }

        const policyCheck = validatePasswordPolicy(novaSenha);
        if (policyCheck !== true) {
            setErrorMsg(policyCheck);
            return;
        }

        setLoading(true);
        try {
            // Executa a alteração no Auth e remoção da flag mustChangePassword via Cloud Function
            await completeCompulsoryPasswordChangeRemote({ newPassword: novaSenha });

            addToast('Senha atualizada com sucesso! Por favor, faça login com sua nova senha.', 'success');

            setTimeout(async () => {
                await logout();
            }, 1000);

        } catch (error) {
            console.error('Erro na troca de senha obrigatória:', error);
            let msg = 'Falha ao atualizar a senha. Tente novamente.';
            if (error.code === 'auth/requires-recent-login') {
                msg = 'Sua sessão expirou. Faça login novamente.';
                await logout();
            } else if (error.message) {
                msg = error.message;
            }
            setErrorMsg(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="compulsory-password-overlay">
            <div className="compulsory-password-card">
                <div className="compulsory-password-header">
                    <div className="icon-badge">
                        <FaLock />
                    </div>
                    <h2>Atualização de Senha Obrigatória</h2>
                    <p>Sua senha foi redefinida por um administrador. Por razões de segurança, defina uma nova senha pessoal para prosseguir.</p>
                </div>

                {errorMsg && (
                    <div className="compulsory-error-banner" role="alert">
                        {errorMsg}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="compulsory-password-form">
                    <div className="compulsory-form-group">
                        <label htmlFor="novaSenha">Nova Senha</label>
                        <div className="password-input-wrapper">
                            <input
                                id="novaSenha"
                                type={showNova ? 'text' : 'password'}
                                autoComplete="new-password"
                                value={novaSenha}
                                onChange={(e) => setNovaSenha(e.target.value)}
                                placeholder="Digite sua nova senha"
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="toggle-visibility-btn"
                                onClick={() => setShowNova(!showNova)}
                                title={showNova ? 'Ocultar senha' : 'Exibir senha'}
                            >
                                {showNova ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="compulsory-form-group">
                        <label htmlFor="confirmarSenha">Confirmar Nova Senha</label>
                        <div className="password-input-wrapper">
                            <input
                                id="confirmarSenha"
                                type={showConfirmar ? 'text' : 'password'}
                                autoComplete="new-password"
                                value={confirmarSenha}
                                onChange={(e) => setConfirmarSenha(e.target.value)}
                                placeholder="Confirme sua nova senha"
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="toggle-visibility-btn"
                                onClick={() => setShowConfirmar(!showConfirmar)}
                                title={showConfirmar ? 'Ocultar senha' : 'Exibir senha'}
                            >
                                {showConfirmar ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <div className="password-policy-box">
                        <h4>Requisitos da Senha:</h4>
                        <ul>
                            <li className={novaSenha.length >= 12 ? 'valid' : ''}>Mínimo de 12 caracteres</li>
                            <li className={/[A-Z]/.test(novaSenha) ? 'valid' : ''}>Pelo menos uma letra maiúscula</li>
                            <li className={/[a-z]/.test(novaSenha) ? 'valid' : ''}>Pelo menos uma letra minúscula</li>
                            <li className={/[0-9]/.test(novaSenha) ? 'valid' : ''}>Pelo menos um número</li>
                            <li className={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>?]/.test(novaSenha) ? 'valid' : ''}>Pelo menos um caractere especial</li>
                        </ul>
                    </div>

                    <button type="submit" className="button-primary submit-btn" disabled={loading}>
                        {loading ? <FaSpinner className="icon-spin" /> : <FaCheckCircle />}
                        {loading ? ' Salvando...' : ' Salvar Nova Senha'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default TrocarSenhaObrigatoria;
